import * as ko from 'knockout'
import * as $ from 'jquery';
import 'jquery.easing';
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {
    ChecklistCategoryDto,
    ChecklistDto,
    ChecklistItemDto,
    DataWrapperChecklistCategoryDto,
    DataWrapperChecklistItemDto,
    myTripChecklistApi,
    myTripParticipantsApi,
    ParticipantDto,
    TripDto
} from "../../api/api-wrapper";
import {MenuViewModelContext} from "../menu/menu";
import {autobind, computed, observable, observableArray} from "knockout-decorators";
import globalState from '../../global-state';
import './checklist-item';
import {postbox} from "../../components/util/postbox";
import i18nextko from "../../components/util/i18nko";
import {createConfirmModal} from "../../components/elements/modal/modal";
import {App} from "../../app";
import {
    isAllCategoriesShared,
    isPrivateCategory,
    isSharedCategory,
    isSharedTrip,
    showParticipateOverlay
} from "../share/shareUtils";

/**
 * ChecklistCategoryDto wrapper.
 * Adds properties which are necessary for the view.
 */
export interface ChecklistCategoryEditable {
    dto: ChecklistCategoryDto;
    itemName: string;
    addItemState: boolean;
    recentlyAdded: boolean;
    renameState: boolean;
    collapseState: boolean;
    hideChecked: boolean;
}

/**
 * ChecklistItemDto wrapper.
 * Adds properties which are necessary for the view.
 */
export class ChecklistItemEditable {

    @observable({deep: true, expose: true})
    public dto: ChecklistItemDto;

    @observable({deep: false, expose: true})
    public checkedAnimation: boolean;

    @observable({deep: false, expose: true})
    public deleted: boolean;

    @observable({deep: false, expose: true})
    public recentlyAdded: boolean;

    constructor(dto: ChecklistItemDto) {
        this.dto = Object.assign({}, {handler: null}, dto);
        this.checkedAnimation = false;
        this.deleted = false;
        this.recentlyAdded = false;

        this.isOeamtcItem = this.isOeamtcItem.bind(this);
        this.hasLink = this.hasLink.bind(this);
        this.link = this.link.bind(this);
    }

    /**
     * Is it an öamtc item?
     */
    isOeamtcItem(): boolean {
        return this.dto.info && this.dto.info.trim().length > 0;
    }

    /**
     * Is a link set?
     */
    hasLink(): boolean {
        return this.dto.link && this.dto.link.trim().length > 0;
    }

    /**
     * Get the link or an empty
     * Ensure the link starts with http protocol and not www.
     */
    link(): string {
        if (!this.hasLink()) {
            return "";
        }
        let url = this.dto.link.trim();
        if (!url.startsWith("http")) {
            return "http://" + url;
        }
        return url;
    }
}


class ViewModelContext extends Context {
    $parent: MenuViewModelContext;
    checklist: ChecklistDto;
}

export class ViewModel {

    /**
     * The trip.
     */
    public trip: TripDto;

    /**
     * Flag whether it is the participate view (first category should be closed by default).
     */
    public isParticipateView: boolean;

    /**
     * The checklist categories.
     */
    @observableArray({deep: true, expose: true})
    public categories: ChecklistCategoryEditable[];

    /**
     * The checklist items.
     */
    @observableArray({deep: false, expose: true})
    public items: ChecklistItemEditable[];

    /**
     * Flag whether the checklist is newly created and a hint for settings should be displayed
     */
    @observable({deep: false, expose: false})
    public showSettingsHint: boolean;

    /**
     * Show checked items only?
     * Default value is false.
     */
    public filterChecked: KnockoutObservable<boolean>;

    /**
     * Filter by search term?
     */
    public filterSearchTerm: KnockoutComputed<boolean>;

    /**
     * Search term for filtering items.
     * Passed from menu as the search field is display in the header.
     */
    public searchTerm: KnockoutObservable<string>;

    /**
     * Check whether at least one filter is active.
     * A filter is active if the default value has changed.
     */
    public isAnyFilteredActive: KnockoutComputed<boolean>;

    /**
     * Subscription for filter change to
     * - reset the recentlyAdded flag on categories and items.
     */
    private filterCheckedSubscription: KnockoutSubscription;

    /**
     * Subscription for filter change to
     * - reset the recentlyAdded flag on categories and items.
     */
    private filterSearchTermSubscription: KnockoutSubscription;

    /**
     * Flag whether the the process to add a new category has been started.
     */
    public addCategoryState: KnockoutObservable<boolean>;

    /**
     * Name of a new category.
     */
    public categoryName: KnockoutObservable<string>;

    /**
     * Constructor.
     * @param ctx
     */
    constructor(ctx: ViewModelContext) {
        console.log(ctx.checklist.categories);
        this.trip = globalState.currentTrip;
        this.isParticipateView = typeof ctx.params.shareId !== 'undefined';
        this.categories = this.toChecklistCategoriesEditable(ctx.checklist.categories.sort((item1, item2) => item1.order - item2.order));
        this.items = this.sortItems(this.toChecklistItemsEditable(ctx.checklist.items));
        this.searchTerm = ctx.$parent.searchTerm;
        this.filterChecked = ko.observable(false);
        this.filterSearchTerm = ko.pureComputed(() => this.searchTerm().trim().length > 0);
        this.filterCheckedSubscription = this.filterChecked.subscribe(() => {
            this.resetRecentlyAdded();
        });
        this.filterSearchTermSubscription = this.filterSearchTerm.subscribe(() => {
            this.resetRecentlyAdded();
        });
        this.isAnyFilteredActive = ko.pureComputed(() =>
            this.filterChecked() == true || this.filterSearchTerm() == true
        );

        this.addCategoryState = ko.observable(false);
        this.categoryName = ko.observable("");

        this.showSettingsHint = ctx.$parent.showSettingsHint !== undefined ? ctx.$parent.showSettingsHint : false;
        if (ctx.$parent.showSettingsHint !== undefined) {
            // reset the flag to display the settings hint only once.
            ctx.$parent.showSettingsHint = false;
        }

        this.categoryItems = this.categoryItems.bind(this);
        this.categoryItemsFiltered = this.categoryItemsFiltered.bind(this);
        this.itemsFiltered = this.itemsFiltered.bind(this);
        this.categoryItemsChecked = this.categoryItemsChecked.bind(this);
        this.categoryItemsOverview = this.categoryItemsOverview.bind(this);
        this.toggleCategoryHideChecked = this.toggleCategoryHideChecked.bind(this);
        this.toggleFilterChecked = this.toggleFilterChecked.bind(this);
        this.isCategoryVisible = this.isCategoryVisible.bind(this);
        this.removeItemAnimated = this.removeItemAnimated.bind(this);
        this.toggleRenameCategory = this.toggleRenameCategory.bind(this);
        this.renameCategory = this.renameCategory.bind(this);
        this.toggleAddCategory = this.toggleAddCategory.bind(this);
        this.addCategory = this.addCategory.bind(this);
        this.deleteCategoryConfirmation = this.deleteCategoryConfirmation.bind(this);
        this.deleteCategory = this.deleteCategory.bind(this);
        this.addItem = this.addItem.bind(this);
        this.toChecklistCategoriesEditable = this.toChecklistCategoriesEditable.bind(this);
        this.toChecklistItemsEditable = this.toChecklistItemsEditable.bind(this);
        this.resetRecentlyAdded = this.resetRecentlyAdded.bind(this);
        this.toogleSettingsHint = this.toogleSettingsHint.bind(this);
    }

    /**
     * The trip's participants.
     */
    @computed({pure: true})
    public get participants(): ParticipantDto[] {
        return globalState.currentParticipants;
    }

    /**
     * Get all items for the given category.
     */
    categoryItems(categoryId: number): KnockoutComputed<ChecklistItemEditable[]> {
        return ko.pureComputed(() => this.items.filter(item => item.dto.category == categoryId));
    }

    /**
     * Get the filtered items for the given category.
     * Items might be filtered by the following properties: checked, important, handBaggage, Öamtc item, search term.
     */
    categoryItemsFiltered(category: ChecklistCategoryEditable): KnockoutComputed<ChecklistItemEditable[]> {
        return ko.pureComputed(() =>
            this.categoryItems(category.dto.id)().filter(item => {
                if (item.recentlyAdded) {
                    return true;
                }
                // Each category has a flag whether checked items should be hidden - which is true by default.
                // If the global filter to view checked items only is on, the hideUnchecked property of the category is ignored.
                if (category.hideChecked && item.dto.checked == true && !this.filterChecked()) {
                    return false;
                }
                if (this.filterChecked() && item.dto.checked == false) {
                    return false;
                }
                if (this.filterSearchTerm()) {
                    return this.searchTermMatches(item, this.searchTerm())
                }
                return true;
            })
        );
    }

    /**
     * Get the filtered items for all categories.
     * Used to display a message for an empty search result.
     */
    itemsFiltered(): KnockoutComputed<ChecklistItemEditable[]> {
        return ko.pureComputed(() => {
            let items: ChecklistItemEditable[] = Array();
            this.categories.forEach(category => {
                let filteredItems: ChecklistItemEditable[] = this.categoryItemsFiltered(category)();
                items = items.concat(filteredItems);
            });
            return items;
        });
    }


    /**
     * Check if the button to add a category is visible.
     *
     * The button is always visible except if the user is filtering but nothing is found.
     * In this case a message is displayed without the button to add a category.
     *
     */
    isAddCategoryVisible(): KnockoutComputed<boolean> {
        return ko.pureComputed(() => {
            return (this.isAnyFilteredActive() && this.itemsFiltered()().length < 1) ? false : true;
        });
    }

    /**
     * Search for the term within the item name.
     * Search is performed lowercase. Whitespace is used to split the term. All terms must match.
     *
     * @param item
     * @param searchTerm
     */
    private searchTermMatches(item: ChecklistItemEditable, searchTerm: string) {
        const name = item.dto.name.trim().toLowerCase();
        const terms: string[] = searchTerm.trim().toLowerCase().split(" ");

        // the item name must match all search terms
        return terms.every(term => {
            return name.indexOf(term) > -1;

        });
    }

    /**
     * Get the checked items for the given category.
     */
    categoryItemsChecked(categoryId: number): KnockoutComputed<ChecklistItemEditable[]> {
        return ko.pureComputed(() =>
            this.categoryItems(categoryId)().filter(item => item.dto.checked)
        );
    }

    /**
     * Get the overview text for for the given category.
     * Returns the number of checked and number of total items.
     *
     * @param categoryId
     */
    categoryItemsOverview(categoryId: number): KnockoutComputed<string> {
        return ko.pureComputed(() =>
            this.categoryItemsChecked(categoryId)().length + "/" + this.categoryItems(categoryId)().length
        );
    }

    /**
     * Removes an item with an animation.
     * The element has to be removed manually from the DOM at the end of the animation.
     *
     * @param elem
     * @param idx
     * @param item
     */
    removeItemAnimated(elem: Node, idx: Number, item: ChecklistItemEditable) {
        if (elem.nodeType === 1) {
            if (item.checkedAnimation) {
                $(elem).fadeOut({
                        duration: 800,
                        easing: 'easeInExpo',
                        complete: function() {
                            $(elem).remove();
                            item.checkedAnimation = false;
                        }
                    }
                );
            } else if (item.deleted) {
                $(elem).fadeOut({
                        duration: 200,
                        easing: 'easeInExpo',
                        complete: function() {
                            $(elem).remove();
                        }
                    }
                );
            } else {
                $(elem).remove();
            }
        }
    }

    /**
     * Check whether the category is visible.
     *
     * @param category
     */
    isCategoryVisible(category: ChecklistCategoryEditable) {
        return ko.pureComputed(() => {
            // new categories are always visible
            if (category.recentlyAdded) {
                return true
            }
                // show empty category if there is no filter active
            // otherwise it is impossible to add items to an empty category
            else if (!this.isAnyFilteredActive()) {
                return true;
            }
            // Hide empty categories if the view is filtered
            return this.categoryItemsFiltered(category)().length > 0;
        });
    }

    /**
     * Toggle the hideChecked property for a specific category.
     * Resets the global checked filter if the hideChecked property is true.
     * Resets the recently added flag on categories and items.
     * Re - sorts the items to display unchecked items first.
     */
    toggleCategoryHideChecked(category: ChecklistCategoryEditable) {
        category.hideChecked = !category.hideChecked;
        this.resetRecentlyAdded();
        this.sortItems(this.items);
        if (category.hideChecked) {
            this.filterChecked(false);

        }
    }

    /**
     * Toggle the status of the global checked filter.
     * If the checked filter is active the hideChecked property of.
     */
    toggleFilterChecked() {
        this.filterChecked(!this.filterChecked());
    }

    /**
     * Sort items by their checked property - unchecked items first.
     * Should be executed on view load and filter change only
     * to avoid UI changes while working on the checklist.
     *
     * 1. Sort items by category
     * 2. Sort items by checked property - false values first
     * 3. Sort items by order property
     *
     * @param items
     */
    private sortItems(items: ChecklistItemEditable[]): ChecklistItemEditable[] {
        return items.sort((item1, item2) => {
            if (item1.dto.category !== item2.dto.category) {
                return item1.dto.category - item2.dto.category;
            }
            if (item1.dto.checked !== item2.dto.checked) {
                return item1.dto.checked ? 1 : -1;
            }
            return item1.dto.order - item2.dto.order;
        });
    }

    /**
     * Toggle the state for adding a category.
     * (display the input field).
     */
    public toggleAddCategory() {
        this.addCategoryState(!this.addCategoryState());
    }

    /**
     * Add a new category.
     */
    public addCategory() {
        if (this.categoryName().trim().length < 1) {
            postbox.addInfo('checklist.errors.emptyCategoryName');
            return;
        }

        globalState.loading(true);
        const category: ChecklistCategoryDto = {
            name: this.categoryName().trim()
        };
        return myTripChecklistApi.createTripChecklistCategory(String(this.trip.id), category)
            .then((dataWrapper: DataWrapperChecklistCategoryDto) => {
                const categoryEditable = this.toChecklistCategoryEditable(dataWrapper.data);
                categoryEditable.recentlyAdded = true;
                this.categories.push(categoryEditable);
                this.categoryName("");
                this.addCategoryState(false);
                globalState.loading(false);
            })
            .catch(() => {
                postbox.addError('checklist.errors.failedToAddCategory');
                globalState.loading(false);
            });
    }

    /**
     * Rename a category.
     *
     * @param category
     */
    public renameCategory(category: ChecklistCategoryEditable) {
        // Validation
        if (category.itemName.trim().length < 1) {
            postbox.addInfo('checklist.errors.emptyCategoryName');
            return;
        }
        // Name has not changed
        if (category.dto.name == category.itemName) {
            this.toggleRenameCategory(category);
            return;
        }

        globalState.loading(true);
        const oldName = category.dto.name;
        category.dto.name = category.itemName;
        return myTripChecklistApi.updateTripChecklistCategory(String(this.trip.id), category.dto.id, category.dto)
            .then(() => {
                this.toggleRenameCategory(category);
                globalState.loading(false);
            })
            .catch(() => {
                category.dto.name = oldName;
                postbox.addError('checklist.errors.failedToRenameCategory');
                globalState.loading(false);
            });
    }

    /**
     * Toggle the rename state of a category.
     *
     * @param category
     */
    public toggleRenameCategory(category: ChecklistCategoryEditable) {
        category.renameState = !category.renameState;
        if (category.renameState) {
            category.itemName = category.dto.name;
        } else {
            category.itemName = "";
        }
    }

    /**
     * Toggle the state for adding a new checklist item.
     * (display the input field).
     */
    public toggleAddItem(category: ChecklistCategoryEditable) {
        category.addItemState = !category.addItemState;
    }

    /**
     * Add a new checklist item.
     */
    public addItem(category: ChecklistCategoryEditable) {
        if (category.itemName.trim().length < 1) {
            postbox.addInfo('checklist.errors.emptyItemName');
            return;
        }

        globalState.loading(true);
        const item: ChecklistItemDto = {
            name: category.itemName.trim(),
            category: category.dto.id,
            checked: false,
            handBaggage: false,
            important: false
        };

        return myTripChecklistApi.createTripChecklistItem(String(this.trip.id), item)
            .then((dataWrapper: DataWrapperChecklistItemDto) => {
                const itemEditable = this.toChecklistItemEditable(dataWrapper.data);
                itemEditable.recentlyAdded = true;
                this.items.push(itemEditable);
                category.addItemState = false;
                category.itemName = "";
                globalState.loading(false);
            })
            .catch(() => {
                postbox.addError('checklist.errors.failedToAddItem');
                globalState.loading(false);
            });
    }

    public deleteCategoryConfirmation(category: ChecklistCategoryEditable) {
        const title = i18nextko.t("checklist.deleteCategory");
        const text = i18nextko.t("checklist.deleteCategoryConfirmation");
        const confirm = i18nextko.t("global.delete");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fo-icon fo-icon-trash";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                return this.deleteCategory(category);
            })
            .catch(() => {
                return false;
            });
    }

    public deleteCategory(category: ChecklistCategoryEditable) {
        globalState.loading(true);

        return myTripChecklistApi.deleteTripChecklistCategory(String(this.trip.id), category.dto.id)
            .then(() => {
                this.categories.splice(this.categories.indexOf(category, 0), 1);
                globalState.loading(false);
            })
            .catch(() => {
                postbox.addError('checklist.errors.failedToDeleteCategory');
                globalState.loading(false);
            });
    }

    public toogleSettingsHint() {
        this.showSettingsHint = !this.showSettingsHint;
    }

    @autobind
    public isSharedCategory(category: ChecklistCategoryDto) {
        return isSharedCategory(this.trip, category);
    }

    @autobind
    public isPrivateCategory(category: ChecklistCategoryDto) {
        return isPrivateCategory(this.trip, category);
    }

    @computed
    public get isSharedTrip() {
        return isSharedTrip(this.trip);
    }

    @computed
    public get isSharedTripWithParticipants() {
        return isSharedTrip(this.trip) && globalState.currentParticipants.length > 1;
    }

    @autobind
    public isAllCategoriesShared() {
         return isAllCategoriesShared(this.categories.map(categoryEditable => categoryEditable.dto));
    }

    @autobind
    public isCategoryDeletable(categoryEditable: ChecklistCategoryEditable) {
        return !this.isSharedTrip ||
            this.isAllCategoriesShared() ||
            (this.isSharedTrip && categoryEditable.dto.owner);
    }

    /**
     * Dispose subscriptions.
     */
    public dispose() {
        this.filterCheckedSubscription.dispose();
        this.filterSearchTermSubscription.dispose();
    }

    /**
     * Convert ChecklistItemDto to ChecklistItemEditable.
     */
    private toChecklistItemEditable(item: ChecklistItemDto): ChecklistItemEditable {
        return new ChecklistItemEditable(item);

    }

    /**
     * Convert ChecklistItemDto[] to ChecklistItemEditable[].
     */
    private toChecklistItemsEditable(items: ChecklistItemDto[]): ChecklistItemEditable[] {
        return items.map(item => this.toChecklistItemEditable(item));
    }

    /**
     * Convert ChecklistCategoryDto to ChecklistCategoryEditable.
     */
    private toChecklistCategoryEditable(item: ChecklistCategoryDto): ChecklistCategoryEditable {
        return {
            dto: item,
            itemName: "",
            addItemState: false,
            recentlyAdded: false,
            renameState: false,
            collapseState: false,
            hideChecked: true
        }
    }

    /**
     * Convert ChecklistCategoryDto[] to ChecklistCategoryEditable[].
     *
     * @param items
     */
    private toChecklistCategoriesEditable(items: ChecklistCategoryDto[]): ChecklistCategoryEditable[] {
        return items.map(item => this.toChecklistCategoryEditable(item));
    }

    /**
     * Resets the recentlyAdded flag on items and categories.
     * Should be executed whenever a filter is enabled or disabled.
     */
    private resetRecentlyAdded() {
        this.items.filter(item => item.recentlyAdded).forEach(item => item.recentlyAdded = false);
        this.categories.filter(cat => cat.recentlyAdded).forEach(cat => cat.recentlyAdded = false);
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./checklist.html'),
    componentName: "checklist",
    loader: (ctx: ViewModelContext) => {
        // Start participate process or show checklist for editing
        if (ctx.$parent.params.shareId) {
            return myTripChecklistApi.getTripChecklistByShareId(ctx.$parent.params.shareId)
                .then(checklist => {
                    ctx.checklist = checklist.data;
                    globalState.currentParticipants = [];

                    // Check login if a user wants to participate
                    return App.loginRequiredModal(
                        function() {
                            // Success callback - show participate overlay
                            return showParticipateOverlay(globalState.currentTrip, ctx.checklist);
                        },
                        function() {
                            // Cancel callback - go to home
                            console.debug("User canceled the login process during participation.")
                            return Router.update('/', {
                                force: true,
                                push: true
                            })
                        },
                        'static');
                });

        } else {
            return Promise.all([
                myTripChecklistApi.getTripChecklist(String(ctx.$parent.params.tripId)).then(checklist => {
                    ctx.checklist = checklist.data;
                    return Promise.resolve();
                }),
                myTripParticipantsApi.getTripParticipants(String(ctx.$parent.params.tripId)).then(participants => {
                    globalState.currentParticipants = participants.data ? participants.data : [];
                    return Promise.resolve();
                })
            ]);
        }
    }
};
